import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Play, Upload, Trash2, Loader2, BellOff, Music } from "lucide-react";
import { BUILTIN_SOUNDS, playSound, stopSound } from "@/lib/reminderSounds";

const MAX_SOUND_BYTES = 5 * 1024 * 1024;

/**
 * Ringtone picker for a reminder: the built-in pack, the user's uploaded
 * sounds (reusable + deletable) and an upload button. `value` is a built-in
 * key, "none", or "custom:<sound_id>".
 */
export default function RingtonePicker({ value, onChange, library, onLibraryChange }) {
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_SOUND_BYTES) {
      toast.error("Sound too large (max 5 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name.replace(/\.[^.]+$/, "").slice(0, 60));
      const r = await api.post("/reminder-sounds", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onLibraryChange?.();
      onChange(`custom:${r.data.sound_id}`);
      toast.success(`"${r.data.name}" added to your sounds`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't upload that sound");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (sound) => {
    setDeletingId(sound.sound_id);
    try {
      stopSound();
      await api.delete(`/reminder-sounds/${sound.sound_id}`);
      if (value === `custom:${sound.sound_id}`) onChange("chime");
      await onLibraryChange?.();
      toast.success("Sound deleted");
    } catch {
      toast.error("Couldn't delete that sound");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="ringtone-picker">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#9CA3AF] flex items-center gap-1.5">
          <Music className="h-3.5 w-3.5" /> Ringtone
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="ringtone-upload-btn"
          className="text-[11px] text-[#3B82F6] hover:text-[#60A5FA] inline-flex items-center gap-1 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          Upload mp3
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,.mp3,.wav,.ogg,.m4a"
          data-testid="ringtone-file-input"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }}
        />
      </div>

      <div className="max-h-44 overflow-y-auto rounded-xl border border-[#27272A] bg-[#0F0F0F] p-1.5 space-y-1">
        <SoundRow
          selected={value === "none"}
          onSelect={() => onChange("none")}
          label="Silent"
          hint="No sound"
          testId="ringtone-option-none"
          icon={<BellOff className="h-3.5 w-3.5 text-[#6B7280]" />}
        />
        {BUILTIN_SOUNDS.map((s) => (
          <SoundRow
            key={s.id}
            selected={value === s.id}
            onSelect={() => { onChange(s.id); playSound(s.id); }}
            onPreview={() => playSound(s.id)}
            label={s.label}
            hint={s.hint}
            testId={`ringtone-option-${s.id}`}
          />
        ))}

        {library.length > 0 && (
          <div className="pt-1.5 mt-1 border-t border-[#1E1E1E]">
            <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-[#6B7280]">My sounds</div>
            {library.map((s) => (
              <SoundRow
                key={s.sound_id}
                selected={value === `custom:${s.sound_id}`}
                onSelect={() => { onChange(`custom:${s.sound_id}`); playSound(`custom:${s.sound_id}`); }}
                onPreview={() => playSound(`custom:${s.sound_id}`)}
                label={s.name}
                hint={s.size ? `${(s.size / 1024).toFixed(0)} KB` : "Uploaded"}
                testId={`ringtone-option-custom-${s.sound_id}`}
                action={
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); remove(s); }}
                    disabled={deletingId === s.sound_id}
                    data-testid={`ringtone-delete-${s.sound_id}`}
                    aria-label={`Delete ${s.name}`}
                    className="h-7 w-7 p-0 rounded-full text-[#6B7280] hover:text-[#F87171] hover:bg-[#EF4444]/10"
                  >
                    {deletingId === s.sound_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SoundRow({ selected, onSelect, onPreview, label, hint, testId, icon, action }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      data-testid={testId}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
        selected ? "bg-[#2563EB]/20 ring-1 ring-[#3B82F6]/50" : "hover:bg-[#1A1A1A]"
      }`}
    >
      <span className="h-3.5 w-3.5 shrink-0 flex items-center justify-center">
        {icon || (
          <span className={`h-2.5 w-2.5 rounded-full border ${selected ? "bg-[#3B82F6] border-[#3B82F6]" : "border-[#3F3F46]"}`} />
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-[#F3F4F6] truncate">{label}</span>
        {hint && <span className="block text-[10px] text-[#6B7280]">{hint}</span>}
      </span>
      {onPreview && (
        <Button
          type="button"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          aria-label={`Play ${label}`}
          data-testid={`${testId}-play`}
          className="h-7 w-7 p-0 rounded-full text-[#9CA3AF] hover:text-[#3B82F6] hover:bg-[#3B82F6]/10"
        >
          <Play className="h-3.5 w-3.5" />
        </Button>
      )}
      {action}
    </div>
  );
}
